import React, { useState } from 'react';
import { CssBaseline, ThemeProvider, Button, Box, Typography, IconButton } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { Routes, Route } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { PasswordUpdatePage } from './pages/PasswordUpdatePage';
import { UserManagement } from './components/UserManagement';
import { Dashboard } from './components/Dashboard';
import { RBACManagement } from './components/RBACManagement';

const getDesignTokens = (mode: 'light' | 'dark') => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: '#7c3aed' },
          background: { default: '#f5f6fa', paper: '#fff' },
        }
      : {
          primary: { main: '#7c3aed' },
          background: { default: '#18181b', paper: '#232326' },
        }),
  },
  typography: {
    fontFamily: 'Inter, Roboto, Arial, sans-serif',
  },
});

function useDarkMode() {
  const [mode, setMode] = useState<'light' | 'dark'>(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return [mode, setMode] as const;
}
function App() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<any>(null);
  const [mode, setMode] = useDarkMode();
  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);
  const lastActivityRef = React.useRef<number>(Date.now());
  const refreshIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const idleCheckIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Constants
  const IDLE_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
  const MAX_SESSION = 60 * 60 * 1000; // 1 hour in milliseconds
  const REFRESH_INTERVAL = 5 * 60 * 1000; // Refresh every 5 minutes if active
  const IDLE_CHECK_INTERVAL = 60 * 1000; // Check idle status every 1 minute



  // Track user activity
  const updateActivity = React.useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Refresh token on activity
  const refreshToken = React.useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        console.log('Token refreshed successfully');
      } else if (response.status === 401) {
        // Token expired or invalid
        handleLogout();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
  }, [token]);

  // Check for idle timeout
  const checkIdleTimeout = React.useCallback(() => {
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    
    if (timeSinceActivity > IDLE_TIMEOUT) {
      console.log('Session expired due to inactivity');
      handleLogout();
    }
  }, []);

  // Setup activity listeners and timers
  React.useEffect(() => {
    if (!token) return;

    // Activity event listeners
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Periodic token refresh (every 5 minutes if active)
    refreshIntervalRef.current = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      // Only refresh if user was active in last 5 minutes
      if (timeSinceActivity < REFRESH_INTERVAL) {
        refreshToken();
      }
    }, REFRESH_INTERVAL);

    // Idle timeout check (every minute)
    idleCheckIntervalRef.current = setInterval(checkIdleTimeout, IDLE_CHECK_INTERVAL);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (idleCheckIntervalRef.current) clearInterval(idleCheckIntervalRef.current);
    };
  }, [token, updateActivity, refreshToken, checkIdleTimeout]);

  // Fetch user info after login
  const fetchUser = async (jwt: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
      }
    } catch {
      setUser(null);
    }
  };

  // Load user on mount if token exists
  React.useEffect(() => {
    if (token) {
      fetchUser(token);
    }
  }, [token]);

  const handleLogin = (jwt: string) => {
    localStorage.setItem('token', jwt);
    setToken(jwt);
    fetchUser(jwt);
    window.location.href = '/';
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/password-reset" element={<PasswordResetPage />} />
        <Route path="/password-update" element={<PasswordUpdatePage />} />
        <Route
          path="/"
          element={
            !token ? (
              <LoginPage onLogin={handleLogin} />
            ) : user ? (
              <Dashboard user={user} mode={mode} setMode={setMode} onLogout={handleLogout} />
            ) : (
              <Typography sx={{ mt: 4 }}>Loading user info...</Typography>
            )
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
